/**
 * 出门在外、挤在一处的几只（2026-09-25 主窗口：去了同一家店、同在驿站、刚出门还在家附近……）：标记不叠在一起，每只都点得到。
 * 在家的由 ./homeCluster 围着小窝排，**小窝和在家的几只不挪**；这里只挪在外面的。
 * - 按屏幕上的距离算碰没碰到：标记框（宠物 52×62、小窝 44×44）之间留不出 8px 的间隔就算碰到；能传递（A 挨着 B、B 挨着 C，三只一堆）。
 *   距离按当前缩放算（每像素多少米 = 高德 map.getResolution()）：放大后分得开的，各自回到自己的位置；缩远了挨近的也会排开。
 * - 碰到小窝那一簇（小窝标记、或排在两侧的在家的几只）的：接着在家的几只往下排，站到小窝旁边的空位上
 *   （同一套位置：先右后左、由近到远，一排 6 只，满了往上一排）——最常见的是刚出门、还在家附近走的那只。
 * - 只在外面彼此挤在一起的：排在这堆第一只的位置两侧，正中留给目的地小旗（同一个地点只画一面旗，见 placeOwners）。
 *   这样一排开要是碰到了小窝那一簇，整堆改排到小窝旁边。
 * - 平移是屏幕像素，写在宠物标记的 transform 上，是“展示偏移”：不写回宠物、不算移动。按当前位置和缩放重算——
 *   但镜头缩放进行中不重算，缩放停下再排（./AmapView，PC-00）。
 * - 同一堆里谁先谁后、谁占哪个位置只看 petId（PC-00，2026-09-27）：不跟着场景顺序变，W1 回包里的顺序变了也不换位。
 */
import { byPetId, homeKey, homeSlot, PET_MARKER_BOX, type MarkerOffset } from "./homeCluster";
import { HOME_MARKER_SIZE, homeMarkerShift } from "./markers";
import type { MapLatLng, WorldPet } from "./model";

/** 标记之间留的空（px），与 ./homeCluster 一致。 */
const GAP = 8;
/** 一排放几只（右 3、左 3）。 */
const PER_ROW = 6;
/** 目的地小旗的半宽（px）：world-map.css 的 .ps-wmap-place 只有定位针时是 30×26。 */
export const PLACE_FLAG_HALF = 15;
/** 纬度 1 度约多少米（经度再乘 cos 纬度）。 */
const METERS_PER_DEGREE = 111_320;

export interface OutItem {
  petId: string;
  /** 这一刻画在哪（./model positionAt）。 */
  point: MapLatLng;
}

/** 小窝那一簇（不挪）：家那个点，和已经排在小窝两侧的在家的几只（./homeCluster homeSlot 0 … residents - 1）。 */
export interface HomeCluster {
  center: MapLatLng;
  residents: number;
}

type Px = { x: number; y: number };
type Box = { left: number; right: number; top: number; bottom: number };

const add = (a: Px, b: Px): Px => ({ x: a.x + b.x, y: a.y + b.y });

/** 宠物标记的框：底边中点在 p（与 world-map.css 的 .ps-wmap-pet 一致）。 */
function petBox(p: Px): Box {
  return { left: p.x - PET_MARKER_BOX.width / 2, right: p.x + PET_MARKER_BOX.width / 2, top: p.y - PET_MARKER_BOX.height, bottom: p.y };
}

/** 小窝标记的框：44×44 的按钮底边中点在家那个点，再按素材锚点平移（./markers homeMarkerShift）。 */
function homeBox(p: Px): Box {
  const shift = homeMarkerShift();
  return { left: p.x + shift.x - HOME_MARKER_SIZE / 2, right: p.x + shift.x + HOME_MARKER_SIZE / 2, top: p.y + shift.y - HOME_MARKER_SIZE, bottom: p.y + shift.y };
}

/** 两个框之间留不出 GAP 的空，就算碰到。 */
function touching(a: Box, b: Box): boolean {
  return a.left < b.right + GAP && b.left < a.right + GAP && a.top < b.bottom + GAP && b.top < a.bottom + GAP;
}

/** 一堆里第 index 只（从 0 数）相对这堆第一只的位置的平移（px）：宠物标记的底边中点挪到这里。 */
export function outSlot(index: number): MarkerOffset {
  // 第一列离那个点：小旗半宽 + 空 + 宠物半宽（正中留给小旗）。
  const firstColumn = PLACE_FLAG_HALF + GAP + PET_MARKER_BOX.width / 2;
  const row = Math.floor(index / PER_ROW);
  const inRow = index % PER_ROW;
  const side = inRow % 2 === 0 ? 1 : -1;
  const rank = Math.floor(inRow / 2);
  const x = side * (firstColumn + rank * (PET_MARKER_BOX.width + GAP));
  const y = -row * (PET_MARKER_BOX.height + GAP);
  return { x: Math.round(x) + 0, y: Math.round(y) + 0 };
}

/** 两个点在屏幕上差多少像素（b 相对 a；x 向东为正，y 向下为正）。 */
function pixelDelta(a: MapLatLng, b: MapLatLng, metersPerPx: number): Px {
  const east = (b.lng - a.lng) * METERS_PER_DEGREE * Math.cos((a.lat * Math.PI) / 180);
  const north = (b.lat - a.lat) * METERS_PER_DEGREE;
  return { x: east / metersPerPx, y: -north / metersPerPx };
}

/**
 * 出门在外的几只 → 需要挪开的那几只的平移（px）。没碰到谁的不在结果里（原地不动）。小窝和在家的几只从不在结果里（不挪）。
 * homes：场景里的小窝那一簇（通常就一个：W1 只给这个家的宠物）。metersPerPx 不是正数（地图还没量出比例尺）时一律不挪。
 */
export function outLayout(input: readonly OutItem[], metersPerPx: number, homes: readonly HomeCluster[] = []): Map<string, MarkerOffset> {
  const offsets = new Map<string, MarkerOffset>();
  if (!(metersPerPx > 0) || input.length === 0 || (input.length < 2 && homes.length === 0)) return offsets;
  // 按 petId 排好再算：谁是一堆里的“第一只”、谁接着排到小窝旁边的哪个空位，都只看 petId。
  const items = [...input].sort(byPetId);
  // 屏幕坐标以第一只为原点。
  const origin = items[0].point;
  const px = items.map((item) => pixelDelta(origin, item.point, metersPerPx));
  const homePx = homes.map((home) => pixelDelta(origin, home.center, metersPerPx));
  // 小窝那一簇占的地方：小窝标记 + 在家的几只；接着排进来的在外面的几只也会加进去。
  const footprints: Box[][] = homes.map((home, h) => [homeBox(homePx[h]), ...Array.from({ length: home.residents }, (_, k) => petBox(add(homePx[h], homeSlot(k))))]);
  // 并查集：节点 0 … n-1 是在外面的几只（按 petId 排好的顺序），n … 是小窝那一簇；碰到的连成一堆。
  const n = items.length;
  const parent = Array.from({ length: n + homes.length }, (_, node) => node);
  const find = (node: number): number => (parent[node] === node ? node : (parent[node] = find(parent[node])));
  const union = (a: number, b: number) => {
    const [x, y] = [find(a), find(b)];
    if (x !== y) parent[Math.max(x, y)] = Math.min(x, y);
  };
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) if (touching(petBox(px[i]), petBox(px[j]))) union(i, j);
    footprints.forEach((boxes, h) => {
      if (boxes.some((box) => touching(petBox(px[i]), box))) union(i, n + h);
    });
  }
  const components = new Map<number, number[]>();
  for (let node = 0; node < n + homes.length; node++) components.set(find(node), [...(components.get(find(node)) ?? []), node]);

  const place = (i: number, target: Px) => offsets.set(items[i].petId, { x: Math.round(target.x - px[i].x) + 0, y: Math.round(target.y - px[i].y) + 0 });
  // 小窝旁边下一个空位：在家的几只之后接着排。
  const nextSlot = homes.map((home) => home.residents);
  const joinHome = (h: number, outs: readonly number[]) => {
    for (const i of outs) {
      const target = add(homePx[h], homeSlot(nextSlot[h]++));
      place(i, target);
      footprints[h].push(petBox(target));
    }
  };
  const loose: number[][] = [];
  for (const members of components.values()) {
    const outs = members.filter((node) => node < n);
    const home = members.find((node) => node >= n);
    if (home !== undefined) joinHome(home - n, outs);
    else if (outs.length >= 2) loose.push(outs);
  }
  // 只在外面彼此挤在一起的：排到第一只两侧；这样一排要是碰到了小窝那一簇，整堆改排到小窝旁边。
  for (const outs of loose) {
    const targets = outs.map((_, k) => add(px[outs[0]], outSlot(k)));
    const hit = footprints.findIndex((boxes) => targets.some((target) => boxes.some((box) => touching(petBox(target), box))));
    if (hit >= 0) joinHome(hit, outs);
    else outs.forEach((i, k) => place(i, targets[k]));
  }
  return offsets;
}

/** 这只此刻要不要画目的地小旗（去的路上、到了）。 */
export function showsPlace(pet: WorldPet): boolean {
  return Boolean(pet.activity.place) && (pet.activity.phase === "going" || pet.activity.phase === "there");
}

/**
 * 同一个地点只画一面小旗：去同一个地方（或已经在那里）的几只，只有场景里排在前面的那只画。
 * 不然同一个位置会叠着几颗一模一样的按钮——只点得到最上面那颗，读屏还把地名念好几遍。
 */
export function placeOwners(pets: readonly WorldPet[]): Set<string> {
  const owners = new Map<string, string>();
  for (const pet of pets) {
    if (!showsPlace(pet)) continue;
    const place = pet.activity.place!;
    const key = `${homeKey(place)}|${place.name}`;
    if (!owners.has(key)) owners.set(key, pet.petId);
  }
  return new Set(owners.values());
}
